import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import OperatorDashboard from "@/pages/operator/Dashboard";
import VisitDetail from "@/pages/operator/VisitDetail";
import Instructions from "@/pages/operator/Instructions";
import Roster from "@/pages/operator/Roster";
import Invoices from "@/pages/operator/Invoices";
import InvoiceDetail from "@/pages/operator/InvoiceDetail";
import Payables from "@/pages/operator/Payables";
import Metrics from "@/pages/operator/Metrics";
import Properties from "@/pages/operator/Properties";
import PropertyDetail from "@/pages/operator/PropertyDetail";
import PropertiesMap from "@/pages/operator/PropertiesMap";
import Owners from "@/pages/operator/Owners";
import SettingsHome from "@/pages/operator/SettingsHome";
import SettingsCategory from "@/pages/operator/SettingsCategory";
import More from "@/pages/operator/More";
import QuotesHome from "@/pages/operator/QuotesHome";
import Crew from "@/pages/operator/Crew";
import Messages from "@/pages/operator/Messages";
import OwnerHome from "@/pages/owner/OwnerHome";
import LogInstruction from "@/pages/owner/LogInstruction";
import OwnerBook from "@/pages/owner/OwnerBook";
import Issues from "@/pages/operator/Issues";
import Jobs from "@/pages/operator/Jobs";
import JobNew from "@/pages/operator/JobNew";
import JobDetail from "@/pages/operator/JobDetail";
import Quotes from "@/pages/operator/Quotes";
import OwnerDetail from "@/pages/operator/OwnerDetail";
import JobHistory from "@/pages/operator/JobHistory";
import QuoteNew from "@/pages/operator/QuoteNew";
import OwnerJobs from "@/pages/owner/OwnerJobs";
import OwnerJobDetail from "@/pages/owner/OwnerJobDetail";
import OwnerRequests from "@/pages/owner/OwnerRequests";
import OwnerInvoices from "@/pages/owner/OwnerInvoices";
import PayInvoice from "@/pages/owner/PayInvoice";
import OwnerSettings from "@/pages/owner/OwnerSettings";
import SubJobs from "@/pages/sub/SubJobs";
import SubJobDetail from "@/pages/sub/SubJobDetail";
import SnowRoute from "@/pages/SnowRoute";
import Platform from "@/pages/Platform";
import PayLink from "@/pages/PayLink";
import QuoteLink from "@/pages/QuoteLink";
import PhotosLink from "@/pages/PhotosLink";
import Leads from "@/pages/Leads";
import BusinessCard from "@/pages/BusinessCard";
import Help from "@/pages/Help";

const OPS = ["operator", "dispatcher"];
const OPERATOR_ONLY = ["operator"];
const OWNER_ONLY = ["owner"];
const SUB_ONLY = ["subcontractor"];
const ANY_ROLE = ["operator", "dispatcher", "owner", "subcontractor"];
const LEAD_ROLES = ["operator", "dispatcher", "subcontractor"];

function Guard({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (!roles.includes(user.role)) return <Navigate to={OPS.includes(user.role) ? "/" : user.role === "owner" ? "/owner" : "/sub"} replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  // Marketing site lives at veloktify.com/rpm-field/ — the app root goes straight to sign-in
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "owner") return <Navigate to="/owner" replace />;
  if (user.role === "subcontractor") return <Navigate to="/sub" replace />;
  return <OperatorDashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
        <Route path="/pay/:token" element={<PayLink />} />
        <Route path="/quote/:token" element={<QuoteLink />} />
        <Route path="/photos/:token" element={<PhotosLink />} />
        <Route path="/card/:oid" element={<BusinessCard />} />
        <Route path="/card/s/:slug" element={<BusinessCard />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Home />} />
      <Route path="/visits/:id" element={<Guard roles={OPS}><VisitDetail /></Guard>} />
      <Route path="/instructions" element={<Guard roles={OPS}><Instructions /></Guard>} />
      <Route path="/roster" element={<Navigate to="/more/crew" replace />} />
      <Route path="/invoices" element={<Guard roles={OPS}><Invoices /></Guard>} />
      <Route path="/invoices/:id" element={<Guard roles={OPS}><InvoiceDetail /></Guard>} />
      <Route path="/payables" element={<Navigate to="/more/crew?tab=payables" replace />} />
      <Route path="/metrics" element={<Guard roles={OPS}><Metrics /></Guard>} />
      <Route path="/properties" element={<Guard roles={OPS}><Properties /></Guard>} />
      <Route path="/properties/map" element={<Guard roles={OPS}><PropertiesMap /></Guard>} />
      <Route path="/properties/:id" element={<Guard roles={OPS}><PropertyDetail /></Guard>} />
      <Route path="/owners" element={<Guard roles={OPS}><Owners /></Guard>} />
      <Route path="/owners/:id" element={<Guard roles={OPS}><OwnerDetail /></Guard>} />
      <Route path="/messages" element={<Guard roles={OPS}><Messages /></Guard>} />
      <Route path="/messages/:number" element={<Guard roles={OPS}><Messages /></Guard>} />
      <Route path="/issues" element={<Guard roles={OPS}><Issues /></Guard>} />
      <Route path="/jobs" element={<Guard roles={OPS}><Jobs /></Guard>} />
      <Route path="/jobs/new" element={<Guard roles={OPS}><JobNew /></Guard>} />
      <Route path="/jobs/:id" element={<Guard roles={OPS}><JobDetail /></Guard>} />
      <Route path="/history" element={<Guard roles={OPS}><JobHistory /></Guard>} />
      <Route path="/quotes" element={<Guard roles={OPS}><QuotesHome /></Guard>} />
      <Route path="/quotes/service" element={<Guard roles={OPS}><Quotes /></Guard>} />
      <Route path="/quotes/new" element={<Guard roles={OPS}><QuoteNew /></Guard>} />
      <Route path="/quotes/:id" element={<Guard roles={OPS}><QuoteNew /></Guard>} />
      <Route path="/settings" element={<Guard roles={OPERATOR_ONLY}><SettingsHome /></Guard>} />
      <Route path="/settings/:cat" element={<Guard roles={OPERATOR_ONLY}><SettingsCategory /></Guard>} />
      <Route path="/more" element={<Guard roles={OPS}><More /></Guard>} />
      <Route path="/more/crew" element={<Guard roles={OPS}><Crew /></Guard>} />
      <Route path="/leads" element={<Guard roles={LEAD_ROLES}><Leads /></Guard>} />
      <Route path="/owner" element={<Guard roles={OWNER_ONLY}><OwnerHome /></Guard>} />
      <Route path="/owner/requests" element={<Guard roles={OWNER_ONLY}><OwnerRequests /></Guard>} />
      <Route path="/owner/requests/new" element={<Guard roles={OWNER_ONLY}><LogInstruction /></Guard>} />
      <Route path="/owner/book" element={<Guard roles={OWNER_ONLY}><OwnerBook /></Guard>} />
      <Route path="/owner/invoices" element={<Guard roles={OWNER_ONLY}><OwnerInvoices /></Guard>} />
      <Route path="/owner/invoices/:id" element={<Guard roles={OWNER_ONLY}><PayInvoice /></Guard>} />
      <Route path="/owner/jobs" element={<Guard roles={OWNER_ONLY}><OwnerJobs /></Guard>} />
      <Route path="/owner/jobs/:id" element={<Guard roles={OWNER_ONLY}><OwnerJobDetail /></Guard>} />
      <Route path="/owner/settings" element={<Guard roles={OWNER_ONLY}><OwnerSettings /></Guard>} />
      <Route path="/sub" element={<Guard roles={SUB_ONLY}><SubJobs /></Guard>} />
      <Route path="/sub/jobs/:id" element={<Guard roles={SUB_ONLY}><SubJobDetail /></Guard>} />
      <Route path="/snow-route" element={<Guard roles={["operator", "dispatcher", "subcontractor"]}><SnowRoute /></Guard>} />
      <Route path="/platform" element={<Guard roles={ANY_ROLE}><Platform /></Guard>} />
      <Route path="/help" element={<Guard roles={ANY_ROLE}><Help /></Guard>} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
